import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';
import TrimNameInput from './TrimNameInput';

const RomanShadeForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;
  const [width, setWidth] = useState(iv?.widthInches != null ? String(iv.widthInches) : '');
  const [height, setHeight] = useState(iv?.heightInches != null ? String(iv.heightInches) : '');
  const [mountType, setMountType] = useState<'inside' | 'outside' | ''>(iv?.mountType || '');
  const [spaceAboveWindowInches, setSpaceAboveWindowInches] = useState(iv?.spaceAboveWindowInches != null ? String(iv.spaceAboveWindowInches) : '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [fabricRepeatInches, setFabricRepeatInches] = useState(iv?.fabricRepeatInches != null ? String(iv.fabricRepeatInches) : '');
  const [fabricWidthInches, setFabricWidthInches] = useState(iv?.fabricWidthInches != null ? String(iv.fabricWidthInches) : '54');
  const [liningType, setLiningType] = useState<'standard' | 'blackout' | 'sheer' | 'none' | ''>(iv?.liningType || 'standard');
  const [interliningType, setInterliningType] = useState<'none' | 'flannel' | ''>(iv?.interliningType === 'flannel' ? 'flannel' : 'none');
  const [trimPlacement, setTrimPlacement] = useState<'none' | 'bottom' | 'sides' | 'sides_and_bottom' | ''>(iv?.trimPlacement || 'none');
  const [trimDescription, setTrimDescription] = useState<string>(iv?.trimDescription || '');
  const [operatingSystem, setOperatingSystem] = useState<'cord_cleat' | 'rollease' | 'somfy_standard' | ''>(iv?.operatingSystem || 'cord_cleat');
  const [flapSizeInches, setFlapSizeInches] = useState(iv?.flapSizeInches != null ? String(iv.flapSizeInches) : '5');
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      const w = parseFloat(width);
      const h = parseFloat(height);
      if (!w || w <= 0 || !h || h <= 0) {
        setError('Width and length must be greater than 0');
        return null;
      }
      if (!mountType) {
        setError('Mount type is required');
        return null;
      }
      setError(null);
      return {
        widthInches: w,
        heightInches: h,
        mountType,
        spaceAboveWindowInches: mountType === 'outside' && spaceAboveWindowInches ? parseFloat(spaceAboveWindowInches) : null,
        fabricDescription: fabricDescription.trim() || null,
        fabricRepeatInches: fabricRepeatInches ? parseFloat(fabricRepeatInches) : null,
        fabricWidthInches: fabricWidthInches ? parseFloat(fabricWidthInches) : 54,
        liningType: liningType || 'standard',
        interliningType: interliningType || 'none',
        trimPlacement: trimPlacement || 'none',
        trimDescription: trimDescription.trim() || null,
        operatingSystem: operatingSystem || 'cord_cleat',
        flapSizeInches: flapSizeInches ? parseFloat(flapSizeInches) : 5,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Mount Type</label>
          <select
            className="input"
            value={mountType}
            onChange={(e) => setMountType(e.target.value as 'inside' | 'outside' | '')}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="inside">Inside Mount (IM)</option>
            <option value="outside">Outside Mount (OM)</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Width (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Length (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            disabled={props.disabled}
          />
        </div>
        {mountType === 'outside' && (
          <div className="form-group" style={{ flex: 1 }}>
            <label>Space Above Window (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
            <input
              type="number"
              min="0"
              step="0.25"
              className="input"
              value={spaceAboveWindowInches}
              onChange={(e) => setSpaceAboveWindowInches(e.target.value)}
              disabled={props.disabled}
            />
          </div>
        )}
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Lining</label>
          <select
            className="input"
            value={liningType}
            onChange={(e) => setLiningType(e.target.value as 'standard' | 'blackout' | 'sheer' | 'none' | '')}
            disabled={props.disabled}
          >
            <option value="standard">Regular ($12.95/yd)</option>
            <option value="blackout">Blackout ($35.95/yd)</option>
            <option value="sheer">Sheer ($21.95/yd)</option>
            <option value="none">None</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Interlining</label>
          <select
            className="input"
            value={interliningType}
            onChange={(e) => setInterliningType(e.target.value as 'none' | 'flannel' | '')}
            disabled={props.disabled}
          >
            <option value="none">None</option>
            <option value="flannel">Flannel ($16.95/yd)</option>
          </select>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Trim</label>
          <select
            className="input"
            value={trimPlacement}
            onChange={(e) => setTrimPlacement(e.target.value as 'none' | 'bottom' | 'sides' | 'sides_and_bottom' | '')}
            disabled={props.disabled}
          >
            <option value="none">None</option>
            <option value="bottom">Along Bottom</option>
            <option value="sides">Along Sides Only</option>
            <option value="sides_and_bottom">Along Sides and Bottom</option>
          </select>
        </div>
        <TrimNameInput value={trimDescription} onChange={setTrimDescription} disabled={props.disabled} />
        <div className="form-group" style={{ flex: 1 }}>
          <label>Operating System</label>
          <select
            className="input"
            value={operatingSystem}
            onChange={(e) => setOperatingSystem(e.target.value as 'cord_cleat' | 'rollease' | 'somfy_standard' | '')}
            disabled={props.disabled}
          >
            <option value="cord_cleat">Cord &amp; Cleat ($75)</option>
            <option value="rollease">Rollease ($135)</option>
            <option value="somfy_standard">Somfy ($450)</option>
          </select>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Repeat (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            placeholder="Pattern repeat, if any"
            value={fabricRepeatInches}
            onChange={(e) => setFabricRepeatInches(e.target.value)}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Flap Size (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={flapSizeInches}
            onChange={(e) => setFlapSizeInches(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Width (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={fabricWidthInches}
            onChange={(e) => setFabricWidthInches(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={fabricDescription}
            onChange={(name, item) => {
              setFabricDescription(name);
              if (item?.widthInches) {
                setFabricWidthInches(String(item.widthInches));
              }
            }}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default RomanShadeForm;
